/** ACT-02: account types and the feature visibility matrix (docs/account-types-spec.md §3, §6, §7, §13). */

export const BUSINESS_TYPE_IDS = [
  "general",
  "online_store",
  "restaurant",
  "clinic",
  "salon",
  "services",
  "real_estate",
  "car_workshop",
  "travel",
  "retail_shop",
] as const;
export type BusinessTypeId = (typeof BUSINESS_TYPE_IDS)[number];

export function isBusinessTypeId(v: unknown): v is BusinessTypeId {
  return typeof v === "string" && (BUSINESS_TYPE_IDS as readonly string[]).includes(v);
}

export type Visibility = "show" | "more" | "hide";

/**
 * Feature keys = the `to` routes of nav-config.ts (NAV_TOP + NAV_GROUPS), `""` written as `home`,
 * plus the six home task cards as `task:<group id>`. Keep in sync with nav-config.ts (a test guards it).
 */
export const FEATURE_KEYS = [
  "home",
  "conversations",
  "groups",
  "contacts",
  "contacts/segments",
  "contacts/lists",
  "contacts/duplicates",
  "contacts/import",
  "pipeline",
  "settings/labels",
  "settings/custom-fields",
  "templates",
  "automation",
  "system-replies",
  "ai-agents",
  "knowledge",
  "gallery",
  "sequences",
  "flows",
  "automations",
  "vertical-packs",
  "forms",
  "automations/order-lifecycle",
  "ai-agents/test-console",
  "ai/kb-fix-queue",
  "ai/faq-mining",
  "broadcasts",
  "vouchers",
  "loyalty",
  "trips",
  "trips/bookings",
  "sell/winback",
  "growth/widget",
  "growth/site-forms",
  "growth/short-links",
  "marketplace",
  "marketplace/submit",
  "sell",
  "store/orders",
  "store",
  "carts",
  "payments",
  "payments/reconciliation",
  "catalog",
  "settings/commerce",
  "quotes",
  "quotes/settings",
  "bookings",
  "bookings/services",
  "bookings/staff",
  "bookings/waitlist",
  "bookings/settings",
  "analytics",
  "analytics/sales",
  "analytics/payments",
  "analytics/entry-points",
  "settings/workspace",
  "channels",
  "settings/notifications",
  "settings/branches",
  "settings/billing",
  "guarantee",
  "migration",
  "settings/team",
  "settings/teams",
  "settings/roles",
  "settings/approvals",
  "getting-started",
  "help-center",
  "settings",
  "settings/api-keys",
  "settings/integrations/webhooks",
  "settings/integrations/google-sheets",
  "settings/integrations/doc-templates",
  "settings/integrations/automations",
  "settings/integrations/credentials",
  "settings/otp",
  "settings/ai/byok",
  "settings/audit-log",
  "task:reply",
  "task:automate",
  "task:offer",
  "task:growth",
  "task:sell",
  "task:bookings",
  "task:numbers",
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/** Never hidden for any type (spec §7 rule 4): inbox, contacts, channels, settings, billing, help. */
export const ALWAYS_VISIBLE: ReadonlySet<string> = new Set([
  "home",
  "conversations",
  "groups",
  "contacts",
  "channels",
  "settings",
  "settings/workspace",
  "settings/billing",
  "guarantee",
  "getting-started",
  "help-center",
]);

/**
 * THE MATRIX: one row per feature key, one cell per account type. Edit a single cell to change it.
 *
 *   S = "show"  core for that business: in the main nav / home.
 *   M = "more"  useful but not core: collapsed under «Advanced / More» (still one click away).
 *   H = "hide"  clearly irrelevant to that business: out of nav, home and search suggestions;
 *               the direct link and the data keep working (spec §7). Setup/admin pages are never H.
 *
 * ALWAYS_VISIBLE keys are not listed: they are "show" for every type. `general` is "show" for all.
 * Every FEATURE_KEYS entry must have a row (a test enforces it): no implicit default.
 */
const S: Visibility = "show";
const M: Visibility = "more";
const H: Visibility = "hide";

type NonGeneral = Exclude<BusinessTypeId, "general">;

/** Cell order of every row below. */
export const MATRIX_TYPES: readonly NonGeneral[] = [
  "online_store",
  "restaurant",
  "clinic",
  "salon",
  "services",
  "real_estate",
  "car_workshop",
  "travel",
  "retail_shop",
];

type Cells = readonly [
  Visibility,
  Visibility,
  Visibility,
  Visibility,
  Visibility,
  Visibility,
  Visibility,
  Visibility,
  Visibility,
];

const MATRIX: Record<
  Exclude<
    FeatureKey,
    | "home"
    | "conversations"
    | "groups"
    | "contacts"
    | "channels"
    | "settings"
    | "settings/workspace"
    | "settings/billing"
    | "guarantee"
    | "getting-started"
    | "help-center"
  >,
  Cells
> = {
  //                               store rest  clinic salon serv  estate workshop travel retail
  // --- Contacts & CRM
  "contacts/segments": [M, M, M, M, M, M, M, M, M],
  "contacts/lists": [M, M, M, M, M, M, M, M, M],
  "contacts/duplicates": [M, M, M, M, M, M, M, M, M],
  "contacts/import": [M, M, M, M, M, S, M, M, M],
  pipeline: [H, H, H, H, S, S, M, S, M],
  "settings/labels": [M, M, M, M, S, S, M, S, M],
  "settings/custom-fields": [M, M, M, M, M, S, M, S, M],
  templates: [S, S, S, S, S, S, S, S, S],
  // --- Automation & AI
  automation: [S, S, S, S, S, S, S, S, S],
  "system-replies": [M, S, S, M, M, M, M, M, M],
  "ai-agents": [S, S, S, S, S, S, S, S, S],
  knowledge: [S, S, S, S, S, S, S, S, S],
  gallery: [M, M, M, M, M, S, M, M, M],
  sequences: [S, M, S, S, M, S, M, S, M],
  flows: [M, M, M, M, M, M, M, M, M],
  automations: [M, M, M, M, M, M, M, M, M],
  "vertical-packs": [M, M, M, M, M, M, M, M, M],
  forms: [M, M, S, M, M, S, M, S, M],
  "automations/order-lifecycle": [M, M, H, H, H, H, M, H, M],
  "ai-agents/test-console": [M, M, M, M, M, M, M, M, M],
  "ai/kb-fix-queue": [M, M, M, M, M, M, M, M, M],
  "ai/faq-mining": [M, M, M, M, M, M, M, M, M],
  // --- Offers & growth
  broadcasts: [S, S, S, S, S, S, S, S, S],
  vouchers: [M, M, H, S, M, H, M, M, M],
  loyalty: [M, M, H, S, M, H, M, M, M],
  // v04: travel pages — shown for the `travel` type only (v01 + v04 merge).
  trips: [H, H, H, H, H, H, H, S, H],
  "trips/bookings": [H, H, H, H, H, H, H, S, H],
  "sell/winback": [M, M, M, S, M, H, M, M, M],
  "growth/widget": [M, M, M, M, M, S, M, S, M],
  "growth/site-forms": [M, M, M, M, M, H, M, H, M],
  "growth/short-links": [M, M, M, M, M, M, M, M, M],
  marketplace: [M, M, M, M, M, M, M, M, M],
  "marketplace/submit": [H, H, H, H, H, H, H, H, H],
  // --- Selling
  sell: [S, S, M, M, M, H, M, M, S],
  "store/orders": [S, S, M, M, M, H, M, H, S],
  store: [S, S, M, M, M, H, M, M, S],
  carts: [S, M, H, H, H, H, H, H, M],
  payments: [S, M, M, M, M, H, M, S, S],
  "payments/reconciliation": [M, M, H, H, M, H, M, M, M],
  catalog: [S, M, H, H, H, H, H, H, S],
  "settings/commerce": [M, M, M, M, M, H, M, M, M],
  // OWN-14 b63: quotes list — core for job-priced businesses (services, real estate, workshop, retail).
  quotes: [M, H, H, H, S, M, S, M, S],
  "quotes/settings": [M, H, H, H, S, S, S, M, S],
  // --- Bookings
  bookings: [H, M, S, S, S, M, S, M, H],
  "bookings/services": [H, M, S, S, S, M, S, M, H],
  "bookings/staff": [H, H, S, S, M, M, S, H, H],
  "bookings/waitlist": [H, M, M, S, M, H, M, H, H],
  "bookings/settings": [H, M, M, M, M, M, M, M, H],
  // --- Numbers
  analytics: [S, S, S, S, S, S, S, S, S],
  "analytics/sales": [S, M, M, M, M, H, M, M, S],
  "analytics/payments": [M, M, H, M, M, H, M, M, M],
  "analytics/entry-points": [M, M, M, M, M, M, M, M, M],
  // --- Account setup & admin (never hidden: at most "more")
  "settings/notifications": [M, M, M, M, M, M, M, M, M],
  "settings/branches": [M, S, M, M, M, M, M, M, M],
  migration: [M, M, M, M, M, M, M, M, M],
  "settings/team": [M, M, M, M, M, M, M, M, M],
  "settings/teams": [M, M, M, M, M, M, M, M, M],
  "settings/roles": [M, M, M, M, M, M, M, M, M],
  "settings/approvals": [M, M, M, M, M, M, M, M, M],
  "settings/api-keys": [M, M, M, M, M, M, M, M, M],
  "settings/integrations/webhooks": [M, M, M, M, M, M, M, M, M],
  "settings/integrations/google-sheets": [M, M, M, M, M, M, M, M, M],
  "settings/integrations/doc-templates": [M, M, M, M, M, M, M, M, M],
  "settings/integrations/automations": [M, M, M, M, M, M, M, M, M],
  "settings/integrations/credentials": [M, M, M, M, M, M, M, M, M],
  "settings/otp": [M, M, M, M, M, M, M, M, M],
  "settings/ai/byok": [M, M, M, M, M, M, M, M, M],
  "settings/audit-log": [M, M, M, M, M, M, M, M, M],
  // --- Home task cards
  "task:reply": [S, S, S, S, S, S, S, S, S],
  "task:automate": [S, S, S, S, S, S, S, S, S],
  "task:offer": [S, S, S, S, S, S, S, S, S],
  "task:growth": [M, M, M, M, M, M, M, M, M],
  "task:sell": [S, S, M, M, M, H, M, M, S],
  "task:bookings": [H, M, S, S, S, M, S, M, H],
  "task:numbers": [S, S, S, S, S, S, S, S, S],
};

/** The raw cells of a feature for the 9 non-general types, or undefined for ALWAYS_VISIBLE keys. */
export function matrixCells(featureKey: string): Cells | undefined {
  return (MATRIX as Record<string, Cells | undefined>)[featureKey];
}

/** The visibility matrix as data: feature -> type -> cell. `general` shows everything. */
export const VISIBILITY_MATRIX: Record<
  string,
  Record<BusinessTypeId, Visibility>
> = Object.fromEntries(
  Object.entries(MATRIX).map(([k, cells]) => [
    k,
    { general: S, ...Object.fromEntries(MATRIX_TYPES.map((t, i) => [t, cells[i]])) },
  ]),
) as Record<string, Record<BusinessTypeId, Visibility>>;

export interface VisibilityOptions {
  /** Feature keys that already hold data for this workspace; they are never hidden (spec §7 rule 4). */
  hasData?: ReadonlySet<string>;
}

/**
 * How a feature shows for an account type. null/unknown type = `general` = everything shown.
 * A hidden feature only leaves the nav, home and search: its direct link still works (spec §7 rule 2).
 * There is deliberately no owner "show all" override (spec §13.2).
 */
export function visibilityFor(
  typeId: string | null | undefined,
  featureKey: string,
  opts: VisibilityOptions = {},
): Visibility {
  if (!isBusinessTypeId(typeId) || typeId === "general") return "show";
  if (ALWAYS_VISIBLE.has(featureKey)) return "show";
  const key = normalizeFeatureKey(featureKey);
  const v = VISIBILITY_MATRIX[key]?.[typeId] ?? "show";
  if (v === "hide" && hasDataFor(opts.hasData, key)) return "show";
  return v;
}

/** B29B-25: keys renamed in FEATURE_KEYS; saved per-workspace data may still use the old spelling. */
const LEGACY_FEATURE_KEYS: Readonly<Record<string, FeatureKey>> = { "flows/vouchers": "vouchers" };

/** Maps a legacy saved feature key to its current key; any other key is returned unchanged. */
export function normalizeFeatureKey(featureKey: string): string {
  return LEGACY_FEATURE_KEYS[featureKey] ?? featureKey;
}

function hasDataFor(hasData: ReadonlySet<string> | undefined, key: string): boolean {
  if (!hasData) return false;
  if (hasData.has(key)) return true;
  for (const saved of hasData) if (normalizeFeatureKey(saved) === key) return true;
  return false;
}

/** Keys the type shows or tucks under "more" (i.e. not hidden), in input order. */
export function filterVisible(
  typeId: string | null | undefined,
  featureKeys: readonly string[],
  opts: VisibilityOptions = {},
): string[] {
  return featureKeys.filter((k) => visibilityFor(typeId, k, opts) !== "hide");
}

/** Admin preview: every feature key bucketed by how the type treats it. */
export function previewForType(
  typeId: string | null | undefined,
  opts: VisibilityOptions = {},
): { shown: string[]; more: string[]; hidden: string[] } {
  const out = { shown: [] as string[], more: [] as string[], hidden: [] as string[] };
  for (const k of FEATURE_KEYS) {
    const v = visibilityFor(typeId, k, opts);
    (v === "show" ? out.shown : v === "more" ? out.more : out.hidden).push(k);
  }
  return out;
}
